import { Component, OnInit } from '@angular/core';
import {
  SessionService,
  ActiveSession,
} from 'src/app/sharedFeatures/services/session.service';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';

/**
 * Component for managing active user sessions
 * SECURITY FIX: VAPT Issue #3 - Iteration 2
 *
 * Allows users to:
 * - View all their active sessions across devices
 * - Revoke specific sessions
 * - See session details (device, location, last activity)
 */
@Component({
  selector: 'dct-active-sessions',
  templateUrl: './active-sessions.component.html',
  styleUrls: ['./active-sessions.component.scss'],
})
export class ActiveSessionsComponent implements OnInit {
  sessions: ActiveSession[] = [];
  loading = false;
  revoking: { [key: string]: boolean } = {};

  constructor(
    private sessionService: SessionService,
    private toastr: ToastrService,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.loadActiveSessions();
  }

  /**
   * Load all active sessions for the current user
   */
  loadActiveSessions(): void {
    this.loading = true;
    this.sessionService.getActiveSessions().subscribe({
      next: sessions => {
        this.sessions = sessions;
        this.loading = false;
      },
      error: error => {
        console.error('Error loading sessions:', error);
        this.toastr.error(
          this.translate.instant('users.activeSessions.loadFailed'),
          this.translate.instant('shared.error')
        );
        this.loading = false;
      },
    });
  }

  /**
   * Revoke a specific session
   */
  revokeSession(sessionId: string, deviceName: string): void {
    if (
      !confirm(
        this.translate.instant('users.activeSessions.confirmRevoke', {
          deviceName,
        })
      )
    ) {
      return;
    }

    this.revoking[sessionId] = true;

    this.sessionService.revokeSession(sessionId).subscribe({
      next: response => {
        this.toastr.success(
          response.message ||
            this.translate.instant('users.activeSessions.revokeSuccess'),
          this.translate.instant('shared.success')
        );
        this.revoking[sessionId] = false;

        // Remove the session from the list
        this.sessions = this.sessions.filter(s => s.id !== sessionId);
      },
      error: error => {
        console.error('Error revoking session:', error);
        this.toastr.error(
          this.translate.instant('users.activeSessions.revokeFailed'),
          this.translate.instant('shared.error')
        );
        this.revoking[sessionId] = false;
      },
    });
  }

  /**
   * Format date for display
   */
  formatDate(date: Date | string): string {
    const d = new Date(date);
    return d.toLocaleString();
  }

  /**
   * Get relative time (e.g., "2 hours ago")
   */
  getRelativeTime(date: Date | string): string {
    const d = new Date(date);
    const now = new Date();
    const diff = now.getTime() - d.getTime();

    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) {
      return this.translate.instant('users.activeSessions.justNow');
    } else if (minutes < 60) {
      return `${minutes} ${this.translate.instant('users.activeSessions.minutesAgo')}`;
    } else if (hours < 24) {
      return `${hours} ${this.translate.instant('users.activeSessions.hoursAgo')}`;
    } else {
      return `${days} ${this.translate.instant('users.activeSessions.daysAgo')}`;
    }
  }

  /**
   * Check if a session is about to expire (within 24 hours)
   */
  isExpiringSoon(expiresAt: Date | string): boolean {
    const expires = new Date(expiresAt);
    const now = new Date();
    const diff = expires.getTime() - now.getTime();
    const hoursUntilExpiry = diff / 3600000;

    return hoursUntilExpiry < 24 && hoursUntilExpiry > 0;
  }

  /**
   * Get Bootstrap icon name for device type
   */
  getDeviceIcon(deviceName: string): string {
    const name = deviceName.toLowerCase();

    if (name.includes('iphone') || name.includes('android')) {
      return 'hdd-fill'; // mobile phone icon
    } else if (name.includes('ipad') || name.includes('tablet')) {
      return 'tablet-fill';
    } else if (
      name.includes('mac') ||
      name.includes('windows') ||
      name.includes('linux')
    ) {
      return 'laptop-fill';
    } else {
      return 'pc-display'; // default desktop icon
    }
  }
}
